import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Header from "../Header";
import { iconsImgs } from "../../utils/icons";
import "./makeofferpages.css";
import Counter from "./Counter";
import ToggleColumn, { ToggleOption } from "./ToggleOptions";
import ListOfClubs from "./ListOfClubs";
import ClubListSkeleton from "./ClubListSkeleton";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import AuthClient from "../../api/AuthClient";
import CreateOfferApi from "../../api/CreatePromoterOffer";
import { offerRequestModel } from "../../api/models/CreateOfferModel";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { OpenClubsContext } from "../../GlobalContext/openClubsContext";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import { fetchPromoterSelectedGroup, usePromoterSelectedGroup } from "../../hooks/usePromoterSelectedGroup";
import { usePromoterGroupOpenClubs } from "../../hooks/usePromoterGroupOpenClubs";
import Gender from "../../api/enums/Gender";
import ReactiveButton from "reactive-button";
import { routes } from "../../routes/routes";
import { SelectedGroupContext } from "../../GlobalContext/groupDetailContext";
import { useClubs } from "../../GlobalContext/selectedClubsContext";
import { useMakeOffer } from "../../GlobalContext/makeOfferContext";
import BookingType from "../../api/enums/BookingType";

const MakeOfferPages: React.FC = () => {
  const guestListBaseAmount = 20;
  const guestListStep = 5;
  const tableBaseAmount = 1000;
  const tableStep = 500;

  const { openClubs } = useContext(OpenClubsContext);
  const { setSelectedGroup } = useContext(SelectedGroupContext);
  const { selectedClubs, clearClubs, setActiveGroup } = useClubs();
  const { clearToggleOptions } = useMakeOffer();
  const { city, groupId } = useParams();
  const location = useLocation();
  const { selectedGroup, requestState } = usePromoterSelectedGroup(groupId);
  const routeState = location.state as { returnTo?: string; preserveOfferState?: boolean } | null;
  const shouldPreserveOfferState = routeState?.preserveOfferState === true;
  const returnTo = routeState?.returnTo ?? (
    city && groupId
      ? routes.PromGroupDetailPage(city, groupId)
      : routes.PromotersMode
  );

  const femaleCount = useMemo(
    () =>
      selectedGroup?.invitations.filter(
        (invitation) => Gender[invitation.gender].toString() === Gender.Female.toString()
      ).length ?? 0,
    [selectedGroup]
  );

  const maleCount = useMemo(
    () =>
      selectedGroup?.invitations.filter(
        (invitation) => Gender[invitation.gender].toString() === Gender.Male.toString()
      ).length ?? 0,
    [selectedGroup]
  );

  const [requestData, setRequestData] = useState<offerRequestModel>({
    GroupId: "",
    ClubId: "",
    MinSpend: 0,
    IsTableBooking: false,
    IsThere: false,
    IsFreeEntry: false,
    IsFreeDrinks: false,
    IsFreeTaxi: false,
    IsFreeDinner: false,
    IsGoodyBag: false,
    IsOther: false
  });
  const [isTableBooking, setIsTableBooking] = useState<boolean>(false);
  const activeBookingType = isTableBooking
    ? BookingType[BookingType.TableBooking]
    : BookingType[BookingType.GuestList];
  const { requestState: openClubsRequestState } = usePromoterGroupOpenClubs(
    selectedGroup,
    activeBookingType
  );
  const [guestListAmount, setGuestListAmount] = useState<number>(guestListBaseAmount);
  const [tableAmount, setTableAmount] = useState<number>(tableBaseAmount);
  const [selectedOpenClubIds, setSelectedOpenClubIds] = useState<string[]>([]);
  const [sendRequestState, setSendRequestState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const isFreeEntrySelected = requestData.IsFreeEntry;

 const hideGuestListEntryPriceFemalesOnlyFree =
    maleCount === 0 && femaleCount > 0 && isFreeEntrySelected;
 const showGuestListEntryPrice =
    !hideGuestListEntryPriceFemalesOnlyFree || guestListAmount === 0;

  const handleTabSelect = (selectedTab: string | null) => {
    if (selectedTab === "Table") {
      setIsTableBooking(true);
      setRequestData((prevRequestData) => ({
        ...prevRequestData,
        IsFreeEntry: false,
      }));
    } else if (selectedTab === "GuestList") {
      setIsTableBooking(false);
    }
  };
  const navigate = useNavigate();

  const mapToggleOptionsToRequestData = useCallback((toggleOptions: ToggleOption[]) => {

    const requestData: offerRequestModel = {
      GroupId: "",
      ClubId: "",
      MinSpend: 0,
      IsTableBooking: false,
      IsThere: false,
      IsFreeEntry: false,
      IsFreeDrinks: false,
      IsFreeTaxi: false,
      IsFreeDinner: false,
      IsGoodyBag: false,
      IsOther: false
    };

    toggleOptions.forEach(option => {

      if (option.label === "I will walk you in myself") {
        requestData.IsThere = option.checked;
      } else if (option.label === "Free Entry") {
        requestData.IsFreeEntry = option.checked;
      } else if (option.label === "Complimentary Drinks") {
        requestData.IsFreeDrinks = option.checked;
      } else if (option.label === "Free Dinner") {
        requestData.IsFreeDinner = option.checked;
      } else if (option.label === "Free Taxi") {
        requestData.IsFreeTaxi = option.checked;
      } else if (option.label === "Queue Jump") {
        requestData.IsGoodyBag = option.checked;
      } else if (option.label === "Is Other") {
        requestData.IsOther = option.checked
      }
    });

    setRequestData(requestData);
  }, []);

  useEffect(() => {
    setActiveGroup(groupId);
    setSelectedOpenClubIds([]);
  }, [groupId, setActiveGroup]);

  useEffect(() => {
    setSelectedOpenClubIds([]);
  }, [activeBookingType]);

  useEffect(() => {
    if (shouldPreserveOfferState || !groupId) {
      return;
    }

    clearToggleOptions(groupId);
  }, [clearToggleOptions, groupId, shouldPreserveOfferState]);

 useEffect(() => {
    if (!isTableBooking && !requestData.IsFreeEntry && guestListAmount === 0) {
      setGuestListAmount(guestListBaseAmount);
    }
  }, [guestListBaseAmount, guestListAmount, isTableBooking, requestData.IsFreeEntry]);

  const toggleSelectedOpenClub = useCallback((clubId: string) => {
    setSelectedOpenClubIds((previousClubIds) =>
      previousClubIds.includes(clubId)
        ? previousClubIds.filter((selectedClubId) => selectedClubId !== clubId)
        : [...previousClubIds, clubId]
    );
  }, []);

  const setGuestListOfferAmount = (amount: number) => {
    const nextAmount = Math.max(0, Math.trunc(amount));
    setGuestListAmount(nextAmount);
    setRequestData((prevRequestData) => ({
      ...prevRequestData,
      IsFreeEntry: nextAmount === 0,
    }));
  };

  const handleGuestListAmountChange = (amount: number) => {
    setGuestListOfferAmount(amount);
  };

  const handleTableAmountChange = (amount: number) => {
    setTableAmount(Math.max(0, Math.trunc(amount)));
  };

  const authClient = new AuthClient();
  const selectedClubIds = useMemo(() => {
    const closedClubIds = selectedClubs.map((club) => club.id);
    return Array.from(new Set([...selectedOpenClubIds, ...closedClubIds]));
  }, [selectedClubs, selectedOpenClubIds]);

  const CreateOffer = async () => {
    if (sendRequestState === "loading") {
      return;
    }

    if (!groupId) {
      return false;
    }

    if (selectedClubIds.length === 0) {
      toast.error("No club selected.", {
        position: toast.POSITION.TOP_CENTER
      });
      return;
    }

    const api = new CreateOfferApi(authClient);
    const baseRequestData: offerRequestModel = {
      ...requestData,
      GroupId: groupId,
      ClubId: "",
      IsTableBooking: isTableBooking,
      MinSpend: isTableBooking
        ? tableAmount
        : (requestData.IsFreeEntry ? 0 : guestListAmount),
    };

    try {
      setSendRequestState("loading");
      const responses = await Promise.allSettled(
        selectedClubIds.map(async (clubId) => {
          const response = await api.createOffer({
            ...baseRequestData,
            ClubId: clubId,
          });

          if (!response || response.statusCode >= 400) {
            throw new Error(`Failed to create offer for club ${clubId}`);
          }

          return response;
        })
      );

      const hasFailure = responses.some((response) => response.status === "rejected");

      if (hasFailure) {
        throw new Error("One or more offers failed to send.");
      }

      const updatedGroup = await fetchPromoterSelectedGroup(groupId);
      if (updatedGroup) {
        setSelectedGroup(updatedGroup);
      }

      clearClubs();
      setSelectedOpenClubIds([]);
      if (groupId) {
        clearToggleOptions(groupId);
      }
      setSendRequestState("success");
      navigate(routes.PromotersMode);
    } catch (error) {
      console.error("Failed to create offer:", error);
      setSendRequestState("error");
      toast.error("Failed to send offer.", {
        position: toast.POSITION.TOP_CENTER
      });
    }
  }

  return (
    <>
      <Header />
      <div className="centerGroup">
        <div className="desktopUI">
          <div className="row userProfile_Header">
            <Breadcrumbs to={returnTo} />
            <div className="d-flex justify-content-end space col-6 d-flex-gap-5">              
              <ReactiveButton
                buttonState={sendRequestState}
                onClick={() => { void CreateOffer() }}
                color={'yellow'}
                idleText={'Send'}
                loadingText={'Sending'}
                successText={'Sent'}
                errorText={'Error'}
                type={'button'}
                style={{ borderRadius: '20px', color: 'white', border: '1px solid #AF975A' }}
                outline={true}
                shadow={false}
                rounded={false}
                size={'small'}
                block={false}
                messageDuration={2000}
                disabled={sendRequestState === "loading"}
                buttonRef={null}
                width={null}
                height={null}
                animation={true}
              />
            </div>
          </div>
          {requestState === "loading" || requestState === "idle" ? (
            <p>Loading group...</p>
          ) : requestState === "error" || !selectedGroup ? (
            <p>Group not found.</p>
          ) : (
            <>
              <div className="align-content-center justify-content-center d-flex col mt-3">
                <img src={iconsImgs.Female} alt="" className="mt-3 Icons" />
                <p className="mt-3 mb-2 yellowTxt">Females {femaleCount}</p>
                <img src={iconsImgs.Male} alt="" className="mt-3 Icons" />
                <p className="mt-3 mb-2 yellowTxt">Males {maleCount}</p>
              </div>
              <Tabs
                defaultActiveKey="GuestList"
                id="fill-tab-example"
                className="mb-3 mt-3"
                fill
                onSelect={handleTabSelect}
              >
                <Tab eventKey="GuestList" title="Guest List">
                  {showGuestListEntryPrice ? (
                    <div className="align-content-center justify-content-start  d-flex col">
                      <p className="yellowTxt m-2 counterTitle">Entry Price:</p>
                      <Counter
                        amount={guestListAmount}
                        onIncrement={() => setGuestListOfferAmount(guestListAmount + guestListStep)}
                        onDecrement={() => setGuestListOfferAmount(Math.max(0, guestListAmount - guestListStep))}
                        onAmountChange={handleGuestListAmountChange}
                      />
                    </div>
                  ) : null}
                </Tab>
                <Tab eventKey="Table" title="Table">
                  <div className="align-content-center justify-content-start  d-flex col">
                    <p className="yellowTxt m-2 counterTitle">Minimum Spend:</p>
                    <Counter
                      amount={tableAmount}
                      onIncrement={() => setTableAmount(prevAmount => prevAmount + tableStep)}
                      onDecrement={() => setTableAmount(prevAmount => Math.max(0, prevAmount - tableStep))}
                      onAmountChange={handleTableAmountChange}
                    />
                  </div>
                </Tab>
              </Tabs>
              <ToggleColumn
                mapToggleOptionsToRequestData={mapToggleOptionsToRequestData}
                isFreeEntryAllowed={!isTableBooking}
                isFreeEntryChecked={isFreeEntrySelected}
              />
              <div className="sClubs mt-3 d-flex align-items-center w-100">
                <p className=" d-flex align-items-center ">
                  <img src={iconsImgs.Star} className="starIcon" alt="star" />
                  Selected Clubs
                </p>
              </div>
              <div className="align-content-center   justify-content-start  d-flex col">
                <p className="yellowTxt m-2">
                  You are not set as in-house at any club. Contact support if you are
                  working directly for a club.
                </p>
              </div>
              {openClubsRequestState === "loading" || openClubsRequestState === "idle" ? (
                <ClubListSkeleton />
              ) : openClubsRequestState === "error" ? (
                <p className="yellowTxt m-3">Could not load clubs.</p>
              ) : (
                <ListOfClubs
                  allClubs={openClubs}
                  selectedOpenClubIds={selectedOpenClubIds}
                  toggleSelectedOpenClub={toggleSelectedOpenClub}
                  cityName={city}
                />
              )}

              <div className="  mb-5 d-flex mt-3  yellowTxt container">
                <Link
                  to={`/make-offer/${groupId}/missing-clubs`}
                  state={{ returnTo }}
                  className="link btnMO justify-content-center mb-5 d-flex mt-3 align-content-center btns col-12 mt-2 mb-2"
                >
                  Add Missing Club
                </Link>
              </div>
            </>
          )}
        </div>
      </div>


      
      <ToastContainer
        progressClassName="toastProgress"
        bodyClassName="toastBody"
      />
    </>
  );
};

export default MakeOfferPages;
